// ============================================================
// Dashboard: project overview (3 looks) — real registry + stats
// ============================================================
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { load, save } from "../../lib/storage";
import { band } from "../../lib/skelettMass";
import { Btn, EmptyState, Icon } from "../../components/ui";
import { ChatWidget } from "../karte/ChatWidget";
import { useStore, useT } from "../../store/useStore";
import { nf, StatusBadge, ThumbFill, thumbQuelle, thumbSrcSet, useAllStats, useProjects, type ApiProject } from "./shared";
import type { Lang } from "../../lib/i18n";

interface CardData {
  project: ApiProject;
  count: number | null;
  area: number | null;
  cards: Array<{ icon: string; label: string; unit: string; value: number | null }>;
}

// compact card number: big values get a "k" suffix, decimals only when fractional
function fmtCard(v: number): string {
  if (Math.abs(v) >= 100000) return nf(Math.round(v / 1000)) + "k";
  if (Math.abs(v) >= 10000) return nf(v / 1000, 1) + "k";
  return Number.isInteger(v) ? nf(v) : nf(v, 1);
}

function ProjectCard({ d, lang, onOpen, feature }: { d: CardData; lang: Lang; onOpen: (p: ApiProject) => void; feature?: boolean }) {
  const p = d.project;
  // Kachelbreite reicht von ~290 px (Bento) bis ~700 px (Aufmacher) — 640 als
  // Grundbreite, 1280 fuer feine Schirme; der Rahmen darunter haelt die Hoehe.
  const quelle = thumbQuelle(p, 640);
  const platzhalter = <div className="grid-bg" style={{ width: "100%", height: "100%", background: "var(--field)" }} />;
  return (
    <div
      onClick={() => onOpen(p)}
      className="card"
      /* Messmarke der Selbstvermessung — siehe uebersichtKey oben. */
      data-ueb-karte=""
      style={{ overflow: "hidden", cursor: "pointer", transition: "transform .28s cubic-bezier(.22,1,.36,1), box-shadow .28s ease, border-color .28s ease", display: "flex", flexDirection: "column", height: "100%" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-5px) scale(1.006)";
        e.currentTarget.style.boxShadow = "var(--shadow-lg)";
        e.currentTarget.style.borderColor = "var(--accent)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "var(--glass-border)";
      }}
    >
      <div style={{ height: feature ? 230 : 170, overflow: "hidden", position: "relative", flex: "none" }}>
        {quelle ? (
          <ThumbFill src={quelle} srcSet={p.thumbUrl ? undefined : thumbSrcSet(p.id, 640, 1280, p.thumbSig)} alt={p.name} posX={p.thumbX} fallback={platzhalter} />
        ) : (
          platzhalter
        )}
        <div style={{ position: "absolute", top: 12, left: 12 }}>
          <span className="badge-auf-bild"><StatusBadge status={p.status} lang={lang} /></span>
        </div>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(8,8,8,0.5), transparent 45%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: 12, left: 14, right: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end", pointerEvents: "none" }}>
          <h3 style={{ color: "#fff", fontSize: feature ? 30 : 23, fontWeight: 600, textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}>{p.name}</h3>
        </div>
      </div>
      <div style={{ padding: feature ? "22px 24px" : "18px 20px", display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
        <p style={{ fontSize: 14, color: "var(--text-dim)", lineHeight: 1.5, flex: 1 }}>{p.description[lang]}</p>
        {/* Reservierte Hoehe: 36 px ist EINE fertige Kennzahlzeile — 14 Polster
            + 21 Zeile + 1 Rand, am echten Aufbau gemessen; zwei, drei, vier
            Umbruchzeilen ergeben 75 / 114 / 153 px (Abstand 18). Mehr zu
            reservieren waere falsch: bei breiten Karten (Atlas ab ~660 px
            Kartenbreite) ist die fertige Zeile wirklich nur eine, und der
            Rest bliebe fuer immer leer. Genau eine Zeile steht dagegen IMMER
            da — ein Projekt ohne konfigurierte Kennzahlen zeigt seine
            Objektzahl, also bekommt niemand eine leere Flaeche.
            Der grosse Sprung (33 -> 114 px) kam ohnehin nicht von der Hoehe,
            sondern vom Zweigwechsel; siehe die Vorbelegung der Kennzahlen
            weiter oben. */}
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", paddingTop: 14, borderTop: "1px solid var(--glass-border)", minHeight: 36 }}>
          {d.cards.length > 0
            ? d.cards.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, color: s.value == null ? "var(--text-mute)" : "var(--accent-text)", opacity: s.value == null ? 0.55 : 1 }} title={s.label}>
                  <Icon name={s.icon} size={17} />
                  <span className="font-mono" style={{ fontSize: 14, fontWeight: 600, color: s.value == null ? "var(--text-mute)" : undefined }}>
                    {s.value == null ? "—" : fmtCard(s.value)}
                    {s.value != null && s.unit ? <span style={{ fontSize: 11, marginLeft: 2, color: "var(--text-mute)" }}> {s.unit}</span> : null}
                  </span>
                  {/* descriptor is the label (falls back to unit) — so a named metric like
                      „Gesamtspielfläche" shows even when a unit is set */}
                  <span style={{ fontSize: 12, color: "var(--text-mute)" }}>{s.label || s.unit}</span>
                </div>
              ))
            : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 7, color: "var(--accent-text)" }}>
                  <Icon name="seal-check" size={17} />
                  {d.count === null ? <span className="skeleton" style={{ display: "inline-block", width: 34, height: 14 }} /> : <span className="font-mono" style={{ fontSize: 14, fontWeight: 600 }}>{nf(d.count)}</span>}
                  <span style={{ fontSize: 12, color: "var(--text-mute)" }}>{lang === "de" ? "Objekte" : "features"}</span>
                </div>
                {d.area !== null && d.area > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 7, color: "var(--accent-text)" }}>
                    <Icon name="vector-two" size={17} />
                    <span className="font-mono" style={{ fontSize: 14, fontWeight: 600 }}>{nf(d.area / 1000, 1)}k</span>
                    <span style={{ fontSize: 12, color: "var(--text-mute)" }}>m²</span>
                  </div>
                )}
              </>
            )}
        </div>
      </div>
    </div>
  );
}

function ProjectRow({ d, lang, onOpen }: { d: CardData; lang: Lang; onOpen: (p: ApiProject) => void }) {
  const p = d.project;
  // 96 x 64 px feste Masse — 160 reicht einfach, 320 fuer doppelte Dichte
  const quelle = thumbQuelle(p, 160);
  return (
    <div
      onClick={() => onOpen(p)}
      className="card"
      data-ueb-karte=""
      style={{ display: "flex", alignItems: "center", gap: 18, padding: 14, cursor: "pointer", transition: "border-color .2s, background .2s" }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--accent-ring)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--glass-border)")}
    >
      {quelle ? (
        <div style={{ position: "relative", width: 96, height: 64, flex: "none" }}>
          <ThumbFill
            src={quelle}
            srcSet={p.thumbUrl ? undefined : thumbSrcSet(p.id, 160, 320, p.thumbSig)}
            alt={p.name}
            radius={12}
            posX={p.thumbX}
            breite={96}
            hoehe={64}
            fallback={<div className="grid-bg" style={{ width: "100%", height: "100%", background: "var(--field)" }} />}
          />
        </div>
      ) : (
        <div className="grid-bg" style={{ width: 96, height: 64, borderRadius: 12, flex: "none", background: "var(--field)" }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h3 style={{ fontSize: 18 }}>{p.name}</h3>
          <span className="badge-auf-bild"><StatusBadge status={p.status} lang={lang} /></span>
        </div>
        <p style={{ fontSize: 13, color: "var(--text-mute)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.description[lang]}</p>
      </div>
      <div className="hide-sm" style={{ display: "flex", gap: 28, paddingRight: 8 }}>
        {d.cards.length > 0
          ? d.cards.map((s, i) => (
              <div key={i} style={{ textAlign: "right", opacity: s.value == null ? 0.55 : 1 }} title={s.label}>
                <div className="font-mono" style={{ fontSize: 17, fontWeight: 600, color: s.value == null ? "var(--text-mute)" : undefined }}>
                  {s.value == null ? "—" : fmtCard(s.value)}
                  {s.value != null && s.unit ? <span style={{ fontSize: 12, color: "var(--text-mute)" }}> {s.unit}</span> : null}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-mute)" }}>{s.label || s.unit}</div>
              </div>
            ))
          : (
            <>
              <div style={{ textAlign: "right" }}>
                <div className="font-mono" style={{ fontSize: 17, fontWeight: 600 }}>{d.count === null ? "…" : nf(d.count)}</div>
                <div style={{ fontSize: 11, color: "var(--text-mute)" }}>{lang === "de" ? "Objekte" : "Features"}</div>
              </div>
              {d.area !== null && d.area > 0 && (
                <div style={{ textAlign: "right" }}>
                  <div className="font-mono" style={{ fontSize: 17, fontWeight: 600 }}>{nf(d.area / 1000, 1)}k</div>
                  <div style={{ fontSize: 11, color: "var(--text-mute)" }}>m²</div>
                </div>
              )}
            </>
          )}
      </div>
      <Icon name="arrow-right" size={18} style={{ color: "var(--text-mute)" }} />
    </div>
  );
}

/** Was die fertige Uebersicht ueber sich preisgibt: wieviele Projekte, und
 *  wie hoch eine Karte in DIESER Ansicht wirklich wird. */
interface UebersichtMass {
  n: number;
  h: number;
}
const uebersichtKey = (look: string, kennung: string) => `uebMass.${kennung}.${look}.b${band()}`;

/** Eine Projektkarte als Silhouette — Bild oben, Text, Kennzahlzeile unten. */
function KartenSkelett({ hoehe, bild }: { hoehe?: number; bild: number }) {
  return (
    <div className="card" style={{ overflow: "hidden", height: hoehe, display: "flex", flexDirection: "column" }}>
      <div className="skeleton" style={{ height: bild, borderRadius: 0, flex: "none" }} />
      <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
        <div className="skeleton" style={{ height: 18, width: "55%" }} />
        <div className="skeleton" style={{ height: 13, width: "85%" }} />
        <div className="skeleton" style={{ height: 13, width: "40%" }} />
        <span style={{ flex: 1 }} />
        {/* Kennzahlzeile ueber der Trennlinie — 14 Polster + 21 Zeile,
            dieselbe Rechnung wie in ProjectCard (minHeight 36). */}
        <div style={{ display: "flex", gap: 18, paddingTop: 14, borderTop: "1px solid var(--glass-border)", minHeight: 36 }}>
          <div className="skeleton" style={{ height: 17, width: 86 }} />
          <div className="skeleton" style={{ height: 17, width: 64 }} />
        </div>
      </div>
    </div>
  );
}

/** Eine Zeile der Listenansicht — Miniatur 96x64 in einer 94 px hohen Karte. */
function ZeilenSkelettUeb() {
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", gap: 18, padding: 14 }}>
      <div className="skeleton" style={{ width: 96, height: 64, borderRadius: 12, flex: "none" }} />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="skeleton" style={{ height: 20, width: "34%" }} />
        <div className="skeleton" style={{ height: 13, width: "58%" }} />
      </div>
      <div className="skeleton" style={{ height: 24, width: 92, flex: "none" }} />
    </div>
  );
}

export function ProjectOverview() {
  const t = useT();
  const lang = useStore((s) => s.lang);
  const auth = useStore((s) => s.auth);
  const dashLook = useStore((s) => s.dashLook);
  const setDashLook = useStore((s) => s.setDashLook);
  const lastProjectId = useStore((s) => s.lastProjectId);
  const navigate = useNavigate();

  const projectsQ = useProjects();
  const statsQ = useAllStats(projectsQ.data);
  const cards: CardData[] = (projectsQ.data ?? []).map((p, i) => ({
    project: p,
    count: statsQ[i]?.data?.count ?? null,
    area: statsQ[i]?.data?.totalAreaM2 ?? null,
    /* Solange /stats laeuft, stehen die Kennzahlen schon aus der
       PROJEKTANTWORT da — nur ohne Werte. Der Server rechnet cards genau aus
       cardStats (die ersten vier, mit denselben Symbolen, Beschriftungen und
       Einheiten), die Zeile hat also von der ersten Zeichnung an ihre
       endgueltige Anzahl Chips.
       Das Nachwachsen von 33 auf 114 px kam vom ZWEIGWECHSEL: bis die Antwort
       da war, zeigte die Karte den Rueckfallzweig (Objekte + m², eine Zeile)
       und sprang dann in den Kennzahlzweig mit drei. Der Wechsel ist damit
       weg. Ein Rest bleibt: an der Stelle der Zahl steht zunaechst „—", das
       sind 17 bis 47 px je Chip weniger, und bei schmalen Karten kann darum
       zuletzt noch eine Umbruchzeile dazukommen.
       Ein Projekt ohne konfigurierte Kennzahlen bleibt beim Rueckfall. */
    cards: statsQ[i]?.data?.cards ?? p.cardStats.slice(0, 4).map((cs) => ({ icon: cs.icon, label: cs.label, unit: cs.unit ?? "", value: null })),
  }));

  // Bezugsprojekt des Chats. Die Uebersicht hat kein „aktuelles" Projekt, das
  // Backend verlangt aber eines (ProjectAccessGuard loest die Projekt-ID aus dem
  // Rumpf auf; ohne sie 404). Dieselbe Wahl wie die Karte trifft — zuletzt
  // benutztes Projekt, sonst das erste der Liste (KarteScreen.tsx:291), damit
  // Karte und Uebersicht nicht auseinanderlaufen. Die Liste enthaelt nur
  // Projekte, in denen der Nutzer Mitglied ist (Admins: alle), die Wahl kann
  // also kein fremdes Projekt treffen.
  const chatProject = (projectsQ.data ?? []).find((p) => p.id === lastProjectId) ?? projectsQ.data?.[0];

  const open = (p: ApiProject) => navigate(`/project/${p.id}`);
  const totals = cards.reduce(
    (a, c) => ({ count: a.count + (c.count ?? 0), area: a.area + (c.area ?? 0) }),
    { count: 0, area: 0 },
  );
  // while per-project stats stream in, show a dash instead of a misleading 0
  const statsLoading = (projectsQ.data?.length ?? 0) > 0 && statsQ.some((q) => q.isLoading || q.data === undefined);
  const looks = [
    { id: "atlas", icon: "layout", label: "Atlas" },
    { id: "bento", icon: "squares-four", label: "Bento" },
    { id: "minimal", icon: "rows", label: "Liste" },
  ] as const;

  // featured = first project by sort order — no hardcoded favourite
  const featured = cards[0];
  const others = cards.filter((c) => c !== featured);

  /* SELBSTVERMESSUNG der Uebersicht. Die Silhouette oben kann Anzahl und Hoehe
     der Karten beim ersten Zeichnen nicht wissen — beide haengen an der
     Projektliste, auf die sie ja gerade wartet. Der letzte Besuch weiss es.
     Der Merker haengt an der KENNUNG, nicht am Browser: sonst erbte der
     naechste Angemeldete am selben Rechner die Projektzahl des vorigen. */
  const kennung = auth?.id ?? auth?.username ?? "anon";
  const uebKey = useMemo(() => uebersichtKey(dashLook, kennung), [dashLook, kennung]);
  const uebMass = useMemo(() => load<UebersichtMass | null>(uebKey, null), [uebKey]);
  const skelettN = uebMass && uebMass.n > 0 ? Math.min(uebMass.n, 12) : 3;
  const wurzelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!cards.length || statsLoading) return;
    /* Erst messen, wenn die Schriften stehen — die Kartenhoehe haengt am
       Umbruch der Beschreibung, und der haengt an den Schriftmetriken. */
    const id = window.setTimeout(() => {
      void (document.fonts?.ready ?? Promise.resolve()).then(() => {
        const el = wurzelRef.current?.querySelectorAll<HTMLElement>("[data-ueb-karte]");
        if (!el || !el.length) return;
        // Im Raster sind alle Karten einer Zeile gleich hoch; die groesste
        // bestimmt die Zeilenhoehe, also ist sie das richtige Mass.
        const h = Math.round(Math.max(...[...el].map((e) => e.getBoundingClientRect().height)));
        if (!h) return;
        const alt = load<UebersichtMass | null>(uebKey, null);
        if (alt && alt.n === el.length && Math.abs(alt.h - h) <= 1) return;
        save(uebKey, { n: el.length, h });
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [cards.length, statsLoading, uebKey]);

  return (
    <div ref={wurzelRef} style={{ maxWidth: 1180, margin: "0 auto", padding: "32px 28px 60px" }} className="pad-responsive">
      {/* KI-Assistent — gleiche Form wie ueberall (schwebender Knopf unten
          rechts, Fenster darueber), hier mit eigenem Knopf, weil diese Seite
          keinen anderen Einstieg hat.
          Wer ihn sieht, entscheidet das Widget selbst: Admin, oder Add-on „ki"
          gebucht UND eigene Rolle im Bezugsprojekt. Auf dieser Seite kann jeder
          stehen, deshalb ist das hier — anders als auf /admin — die einzige
          Schranke; sie deckt sich mit dem, was der Server ohnehin durchsetzt.
          Der Knopf steht 96 px hoch: die Fusszeile ist 83 px hoch (1 px Rand +
          26 Polster + 30 Logo + 26 Polster) und der Toaster sitzt fest bei
          bottom 20 (Toaster.tsx:55) — beides nachgerechnet; bei 16 laege der
          Knopf am Seitenende auf den Fusszeilen-Links und unter der ersten
          Meldung. */}
      <ChatWidget
        fixed
        projectId={chatProject?.id}
        contextLabel={chatProject?.name}
        launcherBottom={96}
        onMap={(sel) => {
          if (!chatProject) return;
          navigate(`/karte?project=${encodeURIComponent(chatProject.id)}&select=${encodeURIComponent(sel.layerId)}:${sel.fids.map(encodeURIComponent).join(",")}`);
        }}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", justifyContent: "space-between", marginBottom: 26 }}>
        <div>
          <h1 style={{ fontSize: 31 }}>{t("dash.title")}</h1>
          <p style={{ color: "var(--text-mute)", marginTop: 7, maxWidth: 540 }}>{t("dash.sub")}</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="seg" title={t("dash.layout")}>
            {looks.map((l) => (
              <button key={l.id} className={dashLook === l.id ? "active" : ""} onClick={() => setDashLook(l.id)} title={l.label} style={{ padding: "7px 10px" }}>
                <Icon name={l.icon} size={16} />
              </button>
            ))}
          </div>
          {auth?.role === "admin" && (
            <Btn icon="plus" onClick={() => navigate("/admin")}>
              {t("dash.newproject")}
            </Btn>
          )}
        </div>
      </div>

      {/* summary strip — real aggregates */}
      <div className="card summary-strip" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", marginBottom: 28, overflow: "hidden" }}>
        {(
          [
            ["map-pin-area", projectsQ.isLoading ? "…" : String(cards.length), lang === "de" ? "Kommunen" : "Municipalities"],
            ["seal-check", statsLoading ? "…" : nf(totals.count), lang === "de" ? "Objekte gesamt" : "Total features"],
            ["vector-two", statsLoading ? "…" : nf(totals.area) + " m²", lang === "de" ? "Gesamtfläche" : "Total footprint"],
          ] as Array<[string, string, string]>
        ).map(([ic, n, l], i) => (
          <div key={l} className="summary-cell" style={{ padding: "20px 22px", borderLeft: i ? "1px solid var(--glass-border)" : "none", display: "flex", gap: 13, alignItems: "center" }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: "var(--accent-soft)",
                color: "var(--accent-text)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "none",
              }}
            >
              <Icon name={ic} size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="font-mono" style={{ fontSize: 20, fontWeight: 600, whiteSpace: "nowrap" }}>
                {n}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-mute)" }}>{l}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Die Silhouette folgt der GEWAEHLTEN Ansicht.
          Vorher zeichnete sie immer drei Bento-Karten — auch dann, wenn
          darunter der Atlas (ein breiter Aufmacher plus schmale Begleiter) oder
          die Liste (Zeilen von 94 px) erschien. In zwei von drei Ansichten war
          die Form also von vornherein falsch, und das Bild oben war mit 150 px
          selbst in der Bento-Ansicht 20 px zu flach (echt: 170, Aufmacher 230).
          Wieviele Projekte es sind und wie hoch eine Karte wirklich wird, weiss
          der letzte Besuch — dieselbe Selbstvermessung wie bei den
          Seitenskeletten; ohne sie gelten drei Karten und die natuerliche Hoehe. */}
      {projectsQ.isLoading && (
        <div className="stagger" aria-busy>
          {dashLook === "minimal" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {Array.from({ length: skelettN }, (_, i) => (
                <ZeilenSkelettUeb key={i} />
              ))}
            </div>
          ) : dashLook === "atlas" ? (
            <div className="atlas-grid" style={{ display: "grid", gap: 20, gridTemplateColumns: `1.5fr ${Array.from({ length: Math.max(1, skelettN - 1) }, () => "1fr").join(" ")}` }}>
              <KartenSkelett hoehe={uebMass?.h} bild={230} />
              {Array.from({ length: Math.max(0, skelettN - 1) }, (_, i) => (
                <KartenSkelett key={i} hoehe={uebMass?.h} bild={170} />
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit,minmax(290px,1fr))" }}>
              {Array.from({ length: skelettN }, (_, i) => (
                <KartenSkelett key={i} hoehe={uebMass?.h} bild={170} />
              ))}
            </div>
          )}
        </div>
      )}
      {projectsQ.isError && <EmptyState icon="warning" title={lang === "de" ? "Projekte konnten nicht geladen werden" : "Failed to load projects"} sub={String(projectsQ.error)} />}

      {dashLook === "atlas" && featured && (
        <div className="stagger" style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>
          <div style={{ gridColumn: "1 / -1", display: "grid", gap: 20, gridTemplateColumns: `1.5fr ${others.map(() => "1fr").join(" ")}` }} className="atlas-grid">
            <ProjectCard d={featured} lang={lang} onOpen={open} feature />
            {others.map((c) => (
              <ProjectCard key={c.project.id} d={c} lang={lang} onOpen={open} />
            ))}
          </div>
        </div>
      )}
      {dashLook === "bento" && (
        <div className="stagger" style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fit,minmax(290px,1fr))" }}>
          {cards.map((c) => (
            <ProjectCard key={c.project.id} d={c} lang={lang} onOpen={open} />
          ))}
        </div>
      )}
      {dashLook === "minimal" && (
        <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {cards.map((c) => (
            <ProjectRow key={c.project.id} d={c} lang={lang} onOpen={open} />
          ))}
        </div>
      )}

      <style>{`@media(max-width:780px){ .atlas-grid{ grid-template-columns:1fr!important; } .hide-sm{ display:none!important; } }`}</style>
    </div>
  );
}
